import { makeStyles, Tooltip } from '@material-ui/core';
import moment from 'moment';
import { statusColor, statusTitle } from 'components/Home/Forecast/utils';
import { ForecastDto } from 'utils/wemble-api.generated';
import ForecastTooltip from 'components/shared/ForecastTooltip';

export interface DailyStatus {
  workload: number | null;
  isAway: boolean;
}

export type DailyStatusMap = Record<string, DailyStatus>;

const AWAY_COLOR = '#C4C4C4';

const getColor = (day: DailyStatus) => {
  if (day.isAway) return AWAY_COLOR;
  if (day.workload === null) return 'transparent';
  return statusColor(day.workload.toString());
};


const getTooltipText = (date: string, day: DailyStatus) => {
  const dateStr = moment(date).locale('en').format('ddd, MMM D');
  if (day.isAway) return `${dateStr} — Away`;
  if (day.workload === null) return dateStr;
  return `${dateStr} — ${statusTitle(day.workload.toString())}`;
};

const useStyles = makeStyles((theme) => ({
  container: {
    backgroundColor: theme.palette.gray.g7,
    borderRadius: 8,
    padding: '10px 12px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 12,
    [theme.breakpoints.down(1100)]: {
      flexDirection: 'row' as const,
      flexWrap: 'wrap' as const,
    },
    [theme.breakpoints.down('xs')]: {
      padding: '8px 8px',
      gap: 8,
    },
  },
  month: {
    [theme.breakpoints.down(1100)]: {
      flex: '1 1 0',
      minWidth: 0,
      '&:first-child': {
        paddingRight: 10,
        borderRight: `1px solid ${theme.palette.divider}`,
      },
      '&:nth-child(2)': {
        paddingLeft: 10,
      },
    },
  },
  monthLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: 6,
    [theme.breakpoints.down('xs')]: {
      fontSize: 10,
      marginBottom: 4,
    },
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: 4,
    [theme.breakpoints.down('xs')]: {
      gap: 2,
    },
  },
  dayHeader: {
    fontSize: 10,
    fontWeight: 500,
    color: theme.palette.gray.g2,
    textAlign: 'center' as const,
    paddingBottom: 4,
    [theme.breakpoints.down('xs')]: {
      fontSize: 8,
      paddingBottom: 2,
    },
  },
  cell: {
    aspectRatio: '1',
    borderRadius: 6,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 11,
    fontWeight: 500,
    cursor: 'default',
    position: 'relative' as const,
    [theme.breakpoints.down(1100)]: {
      fontSize: 10,
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: 9,
      borderRadius: 4,
    },
  },
  cellEmpty: {},
  cellToday: {
    fontWeight: 700,
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '4px 10px',
    marginTop: 4,
    [theme.breakpoints.down(1100)]: {
      flex: '1 1 100%',
      justifyContent: 'center',
    },
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 9,
    color: theme.palette.gray.g2,
  },
  legendDot: {
    width: 8,
    height: 8,
    borderRadius: 2,
    flexShrink: 0,
  },
}));

const DAY_HEADERS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const LEGEND_ITEMS = [
  { key: '0', label: statusTitle('0'), color: statusColor('0') },
  { key: '1', label: statusTitle('1'), color: statusColor('1') },
  { key: '2', label: statusTitle('2'), color: statusColor('2') },
  { key: '3', label: statusTitle('3'), color: statusColor('3') },
  { key: 'away', label: 'Away', color: AWAY_COLOR },
];

const getForecastForDate = (date: moment.Moment, forecasts: ForecastDto[]): ForecastDto | undefined => {
  return forecasts.find(f =>
    f.startDate && f.endDate &&
    date.isSameOrAfter(moment(f.startDate), 'day') &&
    date.isSameOrBefore(moment(f.endDate), 'day')
  );
};


const MonthCalendar = ({ month, data, forecasts }: { month: moment.Moment; data: DailyStatusMap; forecasts: ForecastDto[] }) => {
  const classes = useStyles();
  const today = moment();
  const startOfMonth = month.clone().startOf('month');
  const daysInMonth = month.clone().endOf('month').date();
  const firstDayOffset = startOfMonth.isoWeekday() - 1;

  return (
    <div className={classes.month}>
      <div className={classes.monthLabel}>
        {month.clone().locale('en').format('MMMM YYYY')}
      </div>
      <div className={classes.grid}>
        {DAY_HEADERS.map((d, i) => (
          <div key={`h-${i}`} className={classes.dayHeader}>{d}</div>
        ))}
        {Array.from({ length: firstDayOffset }).map((_, i) => (
          <div key={`empty-${i}`} className={classes.cellEmpty} />
        ))}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const dayNum = i + 1;
          const date = startOfMonth.clone().date(dayNum);
          const dateStr = date.format('YYYY-MM-DD');
          const isFuture = date.isAfter(today, 'day');
          const isToday = date.isSame(today, 'day');
          const isWeekend = date.isoWeekday() >= 6;
          const dayData = data[dateStr];
          const forecast = isFuture && !isWeekend ? getForecastForDate(date, forecasts) : undefined;

          const hasStatus = !isWeekend && dayData && dayData.workload !== null && !dayData.isAway;
          const isAway = !isWeekend && dayData && dayData.isAway;
          const noData = !isWeekend && !isFuture && (!dayData || (dayData.workload === null && !dayData.isAway));

          let bgColor: string;
          let textColor: string;
          let opacity = 1;

          if (isWeekend) {
            bgColor = 'transparent';
            textColor = 'rgba(0,0,0,0.2)';
          } else if (isFuture && forecast) {
            bgColor = forecast.status === 'away' ? AWAY_COLOR : statusColor(forecast.status);
            textColor = forecast.status === 'away' ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.9)';
            opacity = 0.45;
          } else if (isFuture || noData) {
            bgColor = 'transparent';
            textColor = 'rgba(0,0,0,0.2)';
          } else if (isAway) {
            bgColor = AWAY_COLOR;
            textColor = 'rgba(0,0,0,0.7)';
          } else if (hasStatus) {
            bgColor = getColor(dayData);
            textColor = 'rgba(255,255,255,0.9)';
          } else {
            bgColor = 'transparent';
            textColor = 'rgba(0,0,0,0.5)';
          }

          const historyTooltip = !isFuture && !isWeekend && dayData ? getTooltipText(dateStr, dayData) : '';

          const todayBorder = isToday
            ? bgColor !== 'transparent'
              ? `inset 0 0 0 2px rgba(0,0,0,0.25)`
              : `inset 0 0 0 2px rgba(0,0,0,0.3)`
            : undefined;

          const cellEl = (
            <div
              className={`${classes.cell} ${isToday ? classes.cellToday : ''}`}
              style={{ backgroundColor: bgColor, color: textColor, opacity, boxShadow: todayBorder }}
            >
              {dayNum}
            </div>
          );

          if (isFuture && forecast) {
            return (
              <ForecastTooltip key={dayNum} forecast={forecast}>
                {cellEl}
              </ForecastTooltip>
            );
          }

          return (
            <Tooltip key={dayNum} title={historyTooltip} arrow placement="top">
              {cellEl}
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
};

const StatusHistory = ({ data, forecasts }: { data: DailyStatusMap; forecasts: ForecastDto[] }) => {
  const classes = useStyles();
  const thisMonth = moment().startOf('month');
  const lastMonth = thisMonth.clone().subtract(1, 'month');

  return (
    <div className={classes.container}>
      <MonthCalendar month={lastMonth} data={data} forecasts={forecasts} />
      <MonthCalendar month={thisMonth} data={data} forecasts={forecasts} />
      <div className={classes.legend}>
        {LEGEND_ITEMS.map(item => (
          <div key={item.key} className={classes.legendItem}>
            <div className={classes.legendDot} style={{ backgroundColor: item.color }} />
            {item.label}
          </div>
        ))}
      </div>
    </div>
  );
};

export default StatusHistory;
